import { CAPABILITY_KEYS } from "../hooks/useCompanyCapabilities";

/**
 * Maps staff investor-portal routes to required capability keys.
 * Used by InvestorPortalRoute for direct-route protection.
 */
export function getRequiredCapabilityForPath(pathname) {
  const path = (pathname || "").replace(/\/$/, "");

  if (!path.startsWith("/super_admin/investor_portal")) {
    return null;
  }

  if (
    path.startsWith("/super_admin/investor_portal/investments") ||
    path === "/super_admin/investor_portal/participants"
  ) {
    return CAPABILITY_KEYS.INVESTMENT;
  }

  if (path.startsWith("/super_admin/investor_portal/investors")) {
    return CAPABILITY_KEYS.INVESTOR_PORTAL;
  }

  if (path.startsWith("/super_admin/investor_portal/kyc")) {
    return CAPABILITY_KEYS.INVESTOR_PORTAL;
  }

  if (path.startsWith("/super_admin/investor_portal/website")) {
    return CAPABILITY_KEYS.INVESTOR_PORTAL;
  }

  if (path.startsWith("/super_admin/investor_portal/marketplace")) {
    return CAPABILITY_KEYS.MARKETPLACE;
  }

  if (path.startsWith("/super_admin/investor_portal/requests")) {
    return CAPABILITY_KEYS.REAL_ESTATE;
  }

  if (path === "/super_admin/investor_portal/dashboard") {
    return CAPABILITY_KEYS.INVESTOR_PORTAL;
  }

  return null;
}

function capabilityEnabled(capabilities, key) {
  if (!key || !capabilities) return false;
  if (key === "serviceRequests") {
    return Boolean(capabilities.realEstate);
  }
  return Boolean(capabilities[key]);
}

function stripEmptySections(subMenu = []) {
  const result = [];
  let pendingSection = null;

  subMenu.forEach((sub) => {
    if (sub.type === "section") {
      pendingSection = sub;
      return;
    }
    result.push(sub);
    pendingSection = null;
  });

  return result;
}

function filterSubMenuByCapabilities(subMenu, capabilities) {
  if (!Array.isArray(subMenu)) return [];

  const filtered = subMenu.filter((sub) => {
    if (sub.type === "section") return false;
    if (sub.disabled) return false;
    if (!sub.capabilityKey) return true;
    return capabilityEnabled(capabilities, sub.capabilityKey);
  });

  return stripEmptySections(filtered);
}

/**
 * Filter staff sidebar: each investor module is a separate top-level item.
 */
export function filterStaffSidebarByCapabilities(sideBarData, capabilities) {
  const investorCapabilityKeys = new Set([
    CAPABILITY_KEYS.INVESTMENT,
    CAPABILITY_KEYS.INVESTOR_PORTAL,
    CAPABILITY_KEYS.MARKETPLACE,
    CAPABILITY_KEYS.REAL_ESTATE,
    CAPABILITY_KEYS.COOPERATIVE_REQUEST,
    "serviceRequests",
  ]);

  return sideBarData
    .map((item) => {
      if (!item.capabilityKey || !investorCapabilityKeys.has(item.capabilityKey)) {
        return item;
      }

      if (!capabilityEnabled(capabilities, item.capabilityKey)) {
        return null;
      }

      if (item.subMenu?.length) {
        const subMenu = filterSubMenuByCapabilities(item.subMenu, capabilities);
        if (!subMenu.length) return null;
        return { ...item, subMenu };
      }

      return item;
    })
    .filter(Boolean);
}

/**
 * Filter member/investor sidebar by company capabilities.
 */
export function filterInvestorSidebarByCapabilities(sideBarData, capabilities) {
  const hasAny =
    Boolean(capabilities?.investorPortal) ||
    Boolean(capabilities?.investment) ||
    Boolean(capabilities?.marketplace) ||
    Boolean(capabilities?.realEstate) ||
    Boolean(capabilities?.cooperativeRequest);

  if (!hasAny) {
    return [];
  }

  return (sideBarData || []).filter((item) => {
    const key = item.capabilityKey;
    if (!key) {
      return hasAny;
    }
    if (key === "investment") return Boolean(capabilities.investment);
    if (key === "marketplace") return Boolean(capabilities.marketplace);
    if (key === "realEstate") {
      return (
        Boolean(capabilities.realEstate) ||
        Boolean(capabilities.investment) ||
        Boolean(capabilities.marketplace)
      );
    }
    if (key === "investorPortal") return Boolean(capabilities.investorPortal);
    if (key === "anyInvestorFeature") return hasAny;
    return true;
  });
}
